import { API, graphqlOperation } from 'aws-amplify'
import { 
  PicklistStatus,
  OrderType
} from '../constants/EnumType'
import {
  DgtPdOrderPicklist,
  ExtraReturnStatus,
  DgtPdOrderPicklistMovement,
  DgtPdOrderPicklistExtraReturn,
  ExtraReturnType,
  DgtPdOrderPicklistExtraReturnItem,
  DgtPdOrderOperationLog,
  WasteRecordStatus,
  OrderType as OrderTypeEnum,
  DgtPdProductionOrder,
  ReceiptApprover,
  Department
} from '../models';

import {
  listDgtPdOrderPicklists,
  getDgtPdOrderPicklist,
  getDgtPdOrderPicklistExtraReturn,
  listDgtPdOrderPicklistMovements,
  extraReturnByOrderId,
  picklistByReleaseDate,
  picklistByRequestedDate,
  picklistByApprovedDate,
  picklistByCheckedDate,
  picklistByQualityChecked,
  picklistByRecordedDate,
  picklistByCompletedDate,
  extraReturnByApprovedDate,
  extraReturnByRequestedDate,
  extraReturnByCanceledDate,
  extraReturnByWithdrawedDate,
  extraReturnByCheckedDate,
  extraReturnByQualityChecked,
  movementsByApprovedDate,
  movementsByCheckedDate,
  getDgtPdOrderPicklistExtraReturnItem,
  movementsByWasteRecorded,
  orderLogByBulkStatusDate,
  picklistByRequestedDateType,
  extraReturnByApprovedDateTypeStatus,
  orderByTypeSortByDate,
  inspectionLogByOrderAndWc,
  orderReceiptByOrder,
  receiptByCurrentApprover,
  receiptByApprovedByWhDate,
  receiptByApprovedByFaDate
} from '../graphql/queries'

import {
  createDgtPdOrderPicklist,
  updateDgtPdOrderPicklist,
  createDgtPdOrderPicklistMovement,
  updateDgtPdOrderPicklistMovement,
  createDgtPdOrderPicklistExtraReturn,
  updateDgtPdOrderPicklistExtraReturn,
  createDgtPdOrderPicklistExtraReturnItem,
  updateDgtPdOrderPicklistExtraReturnItem,
  updateDgtPdOrderOperationLog,
  updateDgtPdOrderReceipt,
  deleteDgtPdOrderReceipt,
  deleteDgtPdOrderInspectionLog
} from '../graphql/mutations'
import { BulkBatch } from '../pages/Form/EditBatchNo';

import {
  OrderPicklistMovement,
  OrderPicklist,
  OrderPicklistExtra,
  OrderPicklistExtraItem,
  GrapQlVariable,
  OrderOperationLog,
  OrderInspectionLog,
  OrderReceipt
} from '../components/types'

import {
  formatDateObject,
  formatMonthObject
} from '../utilities/Format'

export const getRemotePicklistById = async (id : string) => {
  const listResult: any = await API.graphql({
    query: listDgtPdOrderPicklists,
    variables: { pk: id }
  })
  const picklists: DgtPdOrderPicklist[] = listResult.data?.listDgtPdOrderPicklists.items
  const header = picklists.find(pl => pl.sk === 'DETAIL')!
  const items = picklists.filter(pl => pl.sk.startsWith('ITEM'))
  items.sort((a, b) => b.materialType.localeCompare(a.materialType))
  const comments = picklists.filter(pl => pl.sk.startsWith('COMMENT'))
    .filter(co => co.commentedBy !== 'System')
  return { header, items, comments }
}

export const pdRequestPicklist = async (
  latestHeader: OrderPicklist, username: string) => {
  if (latestHeader) {
    const updatedHeader = await updatePicklist(latestHeader.pk, 'DETAIL', {
      status: PicklistStatus.PD_REQUESTED,
      orderType: latestHeader.orderType,
      division: latestHeader.division,
      requestedDate: formatDateObject(new Date()),
      requestedDateTime: Math.floor(Date.now() / 1000),
      requestedBy: username
    }, latestHeader._version!)
    return updatedHeader
  }
  throw Error('not found')
}

export const postComment = async (orderId: string, comment: string, username: string) => {
  const now = Math.floor(Date.now() / 1000)
  const postResult : any = await API.graphql({
    query: createDgtPdOrderPicklist,
    variables: { input: {
      pk: orderId,
      sk: `COMMENT#${now}`,
      commentText : comment,
      commentedBy : username,
      materialType: '-',
      materialCode: '-',
      materialUnit: '-',
      materialDescription: '-'
    }}
  })
  if (postResult.data) return postResult.data.createDgtPdOrderPicklist
  throw new Error('Post comment error')
}

export const whApprovePicklist = async (
  header: OrderPicklist,
  picklists: OrderPicklist[],
  mvs: OrderPicklistMovement[],
  username: string,
  kiNo: string) => {
  const updateApproveMvs = mvs.filter(mv => mv.movementType === '261')
    .map(mv => updateMovement(mv.orderId, mv.sk, {
      approvedDate: formatDateObject(new Date()),
      approvedDateTime: Math.floor(Date.now() / 1000),
      approvedBy: username
    }, mv._version!))

  await Promise.allSettled(updateApproveMvs)

  const updateApproveItems = picklists.map(pl => 
    updatePicklist(pl.pk, pl.sk, {
      status: PicklistStatus.WH_APPROVED,
      orderType: pl.orderType,
      division: pl.division,
      approvedDateTime: Math.floor(Date.now() / 1000),
      approvedBy: username,
      withdrawQty: mvs
        .filter(mv => mv.movementType === '261' && mv.materialCode === pl.materialCode)
        .reduce((prev, { amount }) => prev + amount, 0)
    }, pl._version!))
  // Note dont need to update approvedDate in item
  
  await Promise.allSettled(updateApproveItems)

  if (header) {
    const updatedHeader = await updatePicklist(header.pk, header.sk, {
      status: PicklistStatus.WH_APPROVED,
      orderType: header.orderType,
      division: header.division,
      approvedDate: formatDateObject(new Date()),
      approvedDateTime: Math.floor(Date.now() / 1000),
      approvedBy: username,
      kiNo
    }, header._version!)
    return updatedHeader
  }
  throw new Error('header not found')
}

export const checkItemMovement = async (mv: OrderPicklistMovement, username: string)
: Promise<OrderPicklistMovement> => {
  if (mv) {
    return await updateMovement(mv.orderId, mv.sk, {
      'checkedDate': formatDateObject(new Date()),
      'checkedDateTime': mv.checkedDateTime,
      'checkedBy': username
    }, mv._version!)
  }
  throw new Error("not found");
}

export const uncheckItemMovement = async (mv: OrderPicklistMovement)
: Promise<OrderPicklistMovement> => {
  if (mv) {
    return await updateMovement(mv.orderId, mv.sk, {
      'checkedDate': null,
      'checkedDateTime': null,
      'checkedBy': null
    }, mv._version!)
  }
  throw new Error("not found");
}

export const pdCheckPicklist = async (latestHeader: OrderPicklist, username: string) => {
  if (latestHeader) {
    const updatedHeader = await updatePicklist(latestHeader.pk, 'DETAIL', {
      status: PicklistStatus.PD_CHECKED,
      orderType: latestHeader.orderType,
      division: latestHeader.division,
      checkedDate: formatDateObject(new Date()),
      checkedDateTime: Math.floor(Date.now() / 1000),
      checkedBy: username
    }, latestHeader._version!)
    return updatedHeader
  }
}

export const getMovementsById = async (id : string, materialCode?: string)
  : Promise<OrderPicklistMovement[]> => {
  let vars: any = { orderId: id }
  if (materialCode) {
    vars = {...vars, sk: { beginsWith: materialCode }, filter: { materialCode: { eq: materialCode } }}
  }
  const listResult: any = await API.graphql({
    query: listDgtPdOrderPicklistMovements,
    variables: vars
  })
  const movements: OrderPicklistMovement[] = 
    listResult.data?.listDgtPdOrderPicklistMovements.items
  return movements
}

export const pdRecordGoodWasteBulkByBatch = async (movements: OrderPicklistMovement[], username: string) : Promise<OrderPicklistMovement[]> => {
  let updateMvs = []
  for (const mv of movements) {
    if (mv) {
      updateMvs.push(updateMovement(mv.orderId, mv.sk, {
        amount: mv.amount || 0,
        usedGoodQty: mv.usedGoodQty || 0,
        otherQty: mv.otherQty || 0,
        usedWasteQty: mv.usedWasteQty || 0,
        returnGoodQty: mv.returnGoodQty || 0,
        returnGoodShrinkQty: mv.returnGoodShrinkQty || 0,
        returnWasteQty: mv.returnWasteQty || 0,
        recordedDateTime: Math.floor(Date.now() / 1000),
        recordedBy: username,
        wasteRecordedDate: (mv.returnWasteQty! > 0 && ['PKG'].includes(mv.materialType!)) ? formatDateObject(new Date()) : null,
        wasteRecordedDateTime: (mv.returnWasteQty! > 0 && ['PKG'].includes(mv.materialType!)) ? Math.floor(Date.now() / 1000) : null,
        wasteRecordedBy: (mv.returnWasteQty! > 0 && ['PKG'].includes(mv.materialType!)) ? username : null,
        wasteRecordedStatus: (mv.returnWasteQty! > 0 && ['PKG'].includes(mv.materialType!)) ? mv.wasteRecordedStatus : null,
        approvedWasteReturnDate: null,
        approvedWasteReturnDateTime: null,
        approvedWasteReturnBy: null,
        returnReasonCode: mv.returnReasonCode || null,
        returnReasonDescription: mv.returnReasonDescription || null
      }, mv._version!))
    }
  }
  const results = await Promise.allSettled(updateMvs)
  const updatedMovements = (results.filter(res => res.status === 'fulfilled') as PromiseFulfilledResult<OrderPicklistMovement>[])
        .map(res => res.value)
  return updatedMovements
}

export const updateWasteRecordedStatus = async (movements: OrderPicklistMovement[], status: WasteRecordStatus, clearWasteRecorded=false, ignoreWasteRecordedStatus:String='') => {
  let updateMvs = []
  for (const mv of movements) {
    if (mv && mv.wasteRecordedStatus === ignoreWasteRecordedStatus) {
      continue
    }
    if (mv) {
      let tempUpdate = {
        wasteRecordedStatus: status ? status : null,
      }
      if(clearWasteRecorded){
        tempUpdate = {...tempUpdate, ...{
            approvedWasteReturnDate: null,
            approvedWasteReturnDateTime: null,
            approvedWasteReturnBy: null,
          }
        }
      }
      updateMvs.push(updateMovement(mv.orderId, mv.sk, tempUpdate, mv._version!))
    }
  }
  await Promise.allSettled(updateMvs)
}

export const pdRecordCompletedPicklist = async (latestHeader: OrderPicklist, username: string) => {
  if (latestHeader) {
    const updatedHeader = await updatePicklist(latestHeader.pk, 'DETAIL', {
      status: PicklistStatus.PD_RECORDED,
      orderType: latestHeader.orderType,
      division: latestHeader.division,
      recordedDate: formatDateObject(new Date()),
      recordedDateTime: Math.floor(Date.now() / 1000),
      recordedBy: username
    }, latestHeader._version!)
    return updatedHeader
  }
  throw Error('not found')
}

export const pdCompletePicklist = async (latestHeader: OrderPicklist, username: string) => {
  if (latestHeader) {
    const updatedHeader = await updatePicklist(latestHeader.pk, 'DETAIL', {
      status: PicklistStatus.PICKLIST_COMPLETED,
      orderType: latestHeader.orderType,
      division: latestHeader.division,
      completedDate: formatDateObject(new Date()),
      completedDateTime: Math.floor(Date.now() / 1000),
      completedBy: username
    }, latestHeader._version!)
    return updatedHeader
  }
  throw Error('not found')
}

export const pdPostReturnPicklist = async (
  picklistHeader: DgtPdOrderPicklist,
  returnItems: OrderPicklistMovement[], 
  username: string) => {
  // insert return doc header
  const postReturnHeader : any = await API.graphql({
    query: createDgtPdOrderPicklistExtraReturn,
    variables: { input: {
      orderId: picklistHeader.pk,
      orderInfo: JSON.stringify(picklistHeader),
      type: ExtraReturnType.RETURN,
      requestedDate: formatDateObject(new Date()),
      requestedBy: username,
      requestedDateTime: Math.floor(Date.now() / 1000)
    }}
  })

  if (postReturnHeader.data) {
    const returnDoc = postReturnHeader.data.createDgtPdOrderPicklistExtraReturn
    
    const goodMovements = returnItems.filter(mv => mv.returnGoodQty! > 0)
    if (goodMovements.length > 0) {
      const insertGoodItemPromises = goodMovements.map(item => API.graphql({
        query: createDgtPdOrderPicklistExtraReturnItem,
        variables: { input: {
          extraReturnId: returnDoc.id,
          orderId: item.orderId,
          materialCode: item.materialCode,
          materialDescription: item.materialDescription,
          materialUnit: item.materialUnit,
          batchLotNo: item.batchLotNo,
          qty: item.returnGoodQty,
          returnReasonCode: item.returnReasonCode,
          returnReasonDescription: item.returnReasonDescription
        }}
      }))
      await Promise.allSettled(insertGoodItemPromises)
    }

    const goodShrinkMovements = returnItems.filter(mv => mv.returnGoodShrinkQty! > 0)
    if (goodShrinkMovements.length > 0) {
      const insertGoodShItemPromises = goodShrinkMovements.map(item => API.graphql({
        query: createDgtPdOrderPicklistExtraReturnItem,
        variables: { input: {
          extraReturnId: returnDoc.id,
          orderId: item.orderId,
          materialCode: item.materialCode,
          materialDescription: item.materialDescription,
          materialUnit: item.materialUnit,
          batchLotNo: item.batchLotNo,
          withShQty: item.returnGoodShrinkQty,
          returnReasonCode: item.returnReasonCode,
          returnReasonDescription: item.returnReasonDescription
        }}
      }))
      await Promise.allSettled(insertGoodShItemPromises)
    }
  
    const wasteMovements = returnItems.filter(mv => mv.returnWasteQty! > 0)
    if (wasteMovements.length > 0) {

      const insertWasteItemPromises = wasteMovements.map(item => API.graphql({
        query: createDgtPdOrderPicklistExtraReturnItem,
        variables: { input: {
          extraReturnId: returnDoc.id,
          orderId: item.orderId,
          materialCode: item.materialCode,
          materialDescription: item.materialDescription,
          materialUnit: item.materialUnit,
          batchLotNo: item.batchLotNo,
          wasteQty: item.returnWasteQty,
          returnReasonCode: item.returnReasonCode,
          returnReasonDescription: item.returnReasonDescription
        }}
      }))
      await Promise.allSettled(insertWasteItemPromises)
    }

    for (const item of returnItems) {
      if (item) {
        await updateMovement(item.orderId, item.sk, {
          returnRefId : returnDoc.id
        }, item._version!)
      }
    }
    return returnDoc
  }
  throw new Error('Create return error')
}

export const getReturnPicklist = async (id : string) : Promise<DgtPdOrderPicklistExtraReturn> => {
  const addFilterQuery = getDgtPdOrderPicklistExtraReturn.replace('extraReturnItems {', 'extraReturnItems(filter: {_deleted: {attributeExists: false}}) {')
  const getResult: any = await API.graphql({
    query: addFilterQuery,
    variables: { id }
  })
  const returnResult = getResult.data?.getDgtPdOrderPicklistExtraReturn
  return { ...returnResult, extraReturnItems: returnResult.extraReturnItems.items }
}

export const cancelExtraReturnPicklist = async (extraResult : OrderPicklistExtra, username: string) => {
  if (extraResult) {
    const updateResult : any = await API.graphql({
      query: updateDgtPdOrderPicklistExtraReturn,
      variables: { input: {
        _version: extraResult._version, 
        id: extraResult.id,
        status: ExtraReturnStatus.CANCELED,
        type: extraResult.type,
        canceledDate: formatDateObject(new Date()),
        canceledDateTime: Math.floor(Date.now() / 1000),
        canceledBy: username
      }}
    })
    return updateResult.data.updateDgtPdOrderPicklistExtraReturn
  }
  throw Error('not found')
}

export const pdPostExtraPicklist = async (
  picklistHeader: DgtPdOrderPicklist,
  items: DgtPdOrderPicklist[],
  remark: string,
  username: string,
  status?: ExtraReturnStatus) : Promise<DgtPdOrderPicklistExtraReturn> => {
  // insert extra doc header
  const now = Math.floor(Date.now() / 1000)
  const postExtraHeader : any = await API.graphql({
    query: createDgtPdOrderPicklistExtraReturn,
    variables: { input: status ? {
      orderId: picklistHeader.pk,
      orderInfo: JSON.stringify(picklistHeader),
      type: 'EXTRA',
      requestedDate: formatDateObject(new Date()),
      requestedBy: username,
      requestedDateTime: now,
      items: JSON.stringify(items.map( itm => ({
        materialCode: itm.materialCode,
        materialDescription: itm.materialDescription,
        materialUnit: itm.materialUnit,
        qty: itm.requestQty || 0
      }))),
      remark: remark,
      status,
      approvedDate: formatDateObject(new Date()),
      approvedBy: username,
      approvedDateTime: now
    } : {
      orderId: picklistHeader.pk,
      orderInfo: JSON.stringify(picklistHeader),
      type: 'EXTRA',
      requestedDate: formatDateObject(new Date()),
      requestedBy: username,
      requestedDateTime: now,
      items: JSON.stringify(items.map( itm => ({
        materialCode: itm.materialCode,
        materialDescription: itm.materialDescription,
        materialUnit: itm.materialUnit,
        qty: itm.requestQty || 0
      }))),
      remark: remark
    }}
  })
  if (postExtraHeader.data) {
    // insert extra doc items / update extra doc id to each item
    const extraDoc = postExtraHeader.data.createDgtPdOrderPicklistExtraReturn
    const insertPromises = items.map(item => API.graphql({
      query: createDgtPdOrderPicklistExtraReturnItem,
      variables: { input: {
        extraReturnId: extraDoc.id,
        orderId: item.pk,
        materialCode: item.materialCode,
        materialDescription: item.materialDescription,
        materialUnit: item.materialUnit,
        qty: item.requestQty || 0
      }}
    }))
    await Promise.allSettled(insertPromises)
    return extraDoc
  }
  throw new Error('Create extra error')
}

export const approveExtraPicklist = async (currentExtra: OrderPicklistExtra, username: string) => {
  if (currentExtra) {
    let tempExtraReturnStatus = ExtraReturnStatus.APPROVED
    const tempExtraItems = currentExtra.items ? JSON.parse(currentExtra.items) : []
    if(tempExtraItems.length === 1){
      const tempMaterialCodeFirst = tempExtraItems[0]
      const DIW = '118003';
      if(tempMaterialCodeFirst?.materialCode === DIW){
        // warehouse skip approve if MaterialCodeFirst = DIW Only
        tempExtraReturnStatus = ExtraReturnStatus.QA_CHECKED
      }
    }
    
    const updateResult : any = await API.graphql({
      query: updateDgtPdOrderPicklistExtraReturn,
      variables: { input: {
        _version: currentExtra._version, 
        id: currentExtra.id,
        status: tempExtraReturnStatus,
        type: currentExtra.type,
        approvedDate: formatDateObject(new Date()),
        approvedDateTime: Math.floor(Date.now() / 1000),
        approvedBy: username
      }}
    })
    return updateResult.data.updateDgtPdOrderPicklistExtraReturn
  }

  throw Error('not found')
}

export const approveReturnPicklist = async (
  extraResult : OrderPicklistExtra,
  username: string, 
  remark: string,
  documentNo: string,
  documentYear: string,
  refReturnDoc? : string
) => {
  if (extraResult) {
    const updateResult : any = await API.graphql({
      query: updateDgtPdOrderPicklistExtraReturn,
      variables: { input: {
        _version: extraResult._version, 
        id: extraResult.id,
        status: ExtraReturnStatus.APPROVED,
        type: extraResult.type,
        approvedDate: formatDateObject(new Date()),
        approvedDateTime: Math.floor(Date.now() / 1000),
        approvedBy: username,
        remark,
        documentNo,
        documentYear,
        kiNo: refReturnDoc || undefined
      }}
    })
    return updateResult.data.updateDgtPdOrderPicklistExtraReturn
  }

  throw Error('not found')
}

export const getExtraPicklist = async (id : string) : Promise<DgtPdOrderPicklistExtraReturn> => {
  const getResult: any = await API.graphql({
    query: getDgtPdOrderPicklistExtraReturn,
    variables: { id }
  })
  const extraResult = getResult.data?.getDgtPdOrderPicklistExtraReturn
  return { ...extraResult, extraReturnItems: extraResult.extraReturnItems.items }
}

export const whApprovePicklistExtra = async (
  extraId: string,
  extraOrder: OrderPicklistExtra, mvs: OrderPicklistMovement[], picklistItems: OrderPicklist[], username: string, kiNo: string) => {
  // note - extraId for make sure that extraOrder is not null
  const updateMvs = mvs.map(mv => updateMovement(mv.orderId, mv.sk, {
    approvedDate: formatDateObject(new Date()),
    approvedDateTime: Math.floor(Date.now() / 1000),
    approvedBy: username,
    extraRefId: extraId
  }, mv._version!))

  await Promise.allSettled(updateMvs)
  
  for (const item of picklistItems) {

    const version = item._version || 1

    const currentReqQty = item.requestQty || 0
    const currentWithDrawQty = item.withdrawQty || 0

    const newQty = mvs.filter(mv => mv.movementType === '261' && mv.materialCode === item.materialCode)
      .reduce((prev, { amount }) => prev + amount, 0)
    const updated = await updatePicklist(item.pk, item.sk, {
      requestQty: currentReqQty === 0 ? item.requestQty : currentReqQty,
      withdrawQty: currentWithDrawQty + newQty
    }, version)
    console.log('update picklist requestQty/withdrawQty', updated)
  }

  if (extraOrder) {
    const updateResult : any = await API.graphql({
      query: updateDgtPdOrderPicklistExtraReturn,
      variables: { input: {
        _version: extraOrder._version, 
        id: extraOrder.id,
        status: ExtraReturnStatus.WITHDRAWED,
        type: extraOrder.type,
        withdrawedDate: formatDateObject(new Date()),
        withdrawedDateTime: Math.floor(Date.now() / 1000),
        withdrawedBy: username,
        kiNo
      }}
    })
    return updateResult.data.updateDgtPdOrderPicklistExtraReturn
  }
  throw Error('not found')
}

export const pdCheckPicklistExtra = async (extraResult : OrderPicklistExtra, username: string) => {
  if (extraResult) {
    const updateResult : any = await API.graphql({
      query: updateDgtPdOrderPicklistExtraReturn,
      variables: { input: {
        _version: extraResult._version, 
        id: extraResult.id,
        status: ExtraReturnStatus.CHECKED,
        type: extraResult.type,
        checkedDate: formatDateObject(new Date()),
        checkedDateTime: Math.floor(Date.now() / 1000),
        checkedBy: username
      }}
    })
    return updateResult.data.updateDgtPdOrderPicklistExtraReturn
  }
  throw Error('not found')
}

export const pdDeprecatedPicklist = async (
  orderId: string, sk: string, version: number, isDeprecated: boolean)
  : Promise<OrderPicklist> => {
  const updateItem = await updatePicklist(orderId, sk, {
    deprecated: isDeprecated
  }, version)
  return updateItem
}

export const createMovement = async (orderId: string, sk: string, batch: BulkBatch) => {
  const postResult : any = await API.graphql({
    query: createDgtPdOrderPicklistMovement,
    variables: { input: {
      orderId,
      sk,
      materialCode: batch.code,
      materialDescription: batch.description || '',
      batchLotNo: batch.newBatchNo,
      materialUnit: '-',
      materialType: 'BULK',
      documentYear: '-',
      documentNo: '-',
      documentItemNo: '0000',
      documentCreatedAt: 0,
      movementType: '261',
      amount: 0,
      approvedBy: batch.approvedBy,
      approvedDateTime: batch.approvedDatetime,
      checkedBy: batch.checkedBy,
      checkedDateTime: batch.checkedDatetime
    } }
  })
  if (postResult.data) return postResult.data.createDgtPdOrderPicklistMovement
  throw new Error('Create movement error')
}

export const updateMovement = async (orderId: string, sk: string, payload: any, version: number) => {
  const postResult : any = await API.graphql({
    query: updateDgtPdOrderPicklistMovement,
    variables: { input: {
      orderId,
      sk,
      _version: version,
      ...payload
    }}
  })
  if (postResult.data) return postResult.data.updateDgtPdOrderPicklistMovement
  throw new Error('Update movement error')
}

export const updatePicklist = async (orderId: string, sk: string, payload: any, version: number) => {
  const postResult : any = await API.graphql({
    query: updateDgtPdOrderPicklist,
    variables: { input: {
      _version: version, 
      pk: orderId, 
      sk,
      ...payload
    }}
  })
  if (postResult.data) return postResult.data.updateDgtPdOrderPicklist
  throw new Error('Update item error')
}

export const getHeaderPicklist = async (orderId : string) : Promise<OrderPicklist> => {
  const getResult: any = await API.graphql({
    query: getDgtPdOrderPicklist,
    variables: { pk: orderId, sk: 'DETAIL' }
  })
  return getResult.data?.getDgtPdOrderPicklist
}

export const whCancelPicklist = async (
  header: OrderPicklist) => {
  if (header) {
    const updatedHeader = await updatePicklist(header.pk, header.sk, {
      status: PicklistStatus.PD_RELEASED,
      orderType: header.orderType,
      division: header.division,
      requestedDate: null,
      requestedDateTime: null,
      requestedBy: null
    }, header._version!)
    return updatedHeader
  }
  throw new Error('header not found')
}

export const updateOrderOperationLog = async (id: string, payload: any, version: number) => {
  const postResult : any = await API.graphql({
    query: updateDgtPdOrderOperationLog,
    variables: { input: {
      id,
      _version: version,
      ...payload
    }}
  })
  if (postResult.data) return postResult.data.updateDgtPdOrderOperationLog
  throw new Error('Update movement error')
}

interface SettleResult {
  status: string
  value: DgtPdOrderPicklist
}
export const whSaveWithShortagePicklist = async (
  header: OrderPicklist,
  picklists: OrderPicklist[],
  mvs: OrderPicklistMovement[], 
  username: string) => {
  const updateApproveMvs = mvs.filter(mv => mv.movementType === '261')
    .map(mv => updateMovement(mv.orderId, mv.sk, {
      approvedDate: formatDateObject(new Date()),
      approvedDateTime: Math.floor(Date.now() / 1000),
      approvedBy: username
    }, mv._version!))

  await Promise.allSettled(updateApproveMvs)

  const updateApproveItems = picklists.map(pl => {
    const actualWithdrawQty = mvs
      .filter(mv => mv.movementType === '261' && mv.materialCode === pl.materialCode)
      .reduce((prev, { amount }) => prev + amount, 0)
    let status = PicklistStatus.WH_APPROVED
    // type must
    // - RAW (without DIW)
    // - PKG
    if (['RAW', 'PKG'].includes(pl.materialType) && pl.materialDescription !== 'DIW' && actualWithdrawQty < pl.requestQty!) {
      status = PicklistStatus.WH_SHORTAGE
    }
    return updatePicklist(pl.pk, pl.sk, {
      status,
      orderType: pl.orderType,
      division: pl.division,
      approvedDateTime: Math.floor(Date.now() / 1000),
      approvedBy: username,
      withdrawQty: actualWithdrawQty
    }, pl._version!)
  })
  
  const updates = await Promise.allSettled(updateApproveItems) as SettleResult[]
  const shortageItems = updates.filter(up => up.value.status === PicklistStatus.WH_SHORTAGE).map(itm => itm.value)

  if (header) {
    const updatedHeader = await updatePicklist(header.pk, header.sk, {
      status: PicklistStatus.WH_SHORTAGE,
      orderType: header.orderType,
      division: header.division,
      shortageItems: JSON.stringify(shortageItems),
      approvedDate: null,
      approvedDateTime: null,
      approvedBy: null
    }, header._version!)
    return updatedHeader
  }
  throw new Error('header not found')
}

export const getComments = async (orderId : string) => {
  const commentResult: any = await API.graphql({
    query: listDgtPdOrderPicklists,
    variables: { pk: orderId, sk: { beginsWith: 'COMMENT#' } }
  })
  const picklists: DgtPdOrderPicklist[] = commentResult.data?.listDgtPdOrderPicklists.items
  const comments = picklists.filter(co => co.commentedBy !== 'System')
  return comments
}

export const cancelMovement = async (orderId: string, sk: string, username: string, latestVersion: number)
: Promise<OrderPicklistMovement> => {
  const updateResult : any = await API.graphql({
    query: updateDgtPdOrderPicklistMovement,
    variables: { input: {
      _version: latestVersion, 
      orderId, sk,
      canceledDateTime: Math.floor(Date.now() / 1000),
      canceledBy: username
    }}
  })
  return updateResult.data.updateDgtPdOrderPicklistMovement
}

export const reOpenMovement = async (orderId: string, sk: string, username: string, latestVersion: number)
: Promise<OrderPicklistMovement> => {
  const updateResult : any = await API.graphql({
    query: updateDgtPdOrderPicklistMovement,
    variables: { input: {
      _version: latestVersion, 
      orderId, sk,
      canceledDateTime: null,
      canceledBy: null
    }}
  })
  return updateResult.data.updateDgtPdOrderPicklistMovement
}

export const extraPicklistsByOrderId = async (orderId: string) => {
  const extraResults: any = await API.graphql({
    query: extraReturnByOrderId,
    variables: { orderId, filter: { type: { eq: ExtraReturnType.EXTRA } } }
  })
  const extras: DgtPdOrderPicklistExtraReturn[] = extraResults.data?.extraReturnByOrderId.items
  extras.sort((a, b) => (a.requestedDateTime || 0) - (b.requestedDateTime || 0))
  return extras
}

export const isWhWithdrawPicklist = async (orderId: string) => {
  const getResult: any = await API.graphql({
    query: getDgtPdOrderPicklist,
    variables: { pk: orderId, sk: 'DETAIL' }
  })
  if (getResult.data?.getDgtPdOrderPicklist) {
    const picklistStatus = getResult.data?.getDgtPdOrderPicklist.status
    const approvedDatetime = getResult.data?.getDgtPdOrderPicklist.approvedDateTime
    if (picklistStatus === PicklistStatus.WH_SHORTAGE || approvedDatetime > 0) return true
  }
  return false
}

export const qaCheckItemMovement = async (mv: OrderPicklistMovement, username: string)
: Promise<OrderPicklistMovement> => {
  if (mv) {
    return await updateMovement(mv.orderId, mv.sk, {
      qualityCheckedDate: formatDateObject(new Date()),
      qualityCheckedDateTime: mv.qualityCheckedDateTime,
      qualityCheckedBy: username
    }, mv._version!)
  }
  throw new Error("not found");
  
}

export const qaUncheckItemMovement = async (mv: OrderPicklistMovement)
: Promise<OrderPicklistMovement> => {
  if (mv) {
    return await updateMovement(mv.orderId, mv.sk, {
      qualityCheckedDate: null,
      qualityCheckedDateTime: null,
      qualityCheckedBy: null
    }, mv._version!)
  }
  throw new Error("not found");
  
}

export const qaCheckPicklist = async (latestHeader: OrderPicklist, username: string) => {
  if (latestHeader) {
    const updatedHeader = await updatePicklist(latestHeader.pk, 'DETAIL', {
      status: PicklistStatus.QA_CHECKED,
      orderType: latestHeader.orderType,
      division: latestHeader.division,
      qualityCheckedDate: formatDateObject(new Date()),
      qualityCheckedDateTime: Math.floor(Date.now() / 1000),
      qualityCheckedBy: username
    }, latestHeader._version!)
    return updatedHeader
  }
}

export const qaCheckPicklistExtra = async (extraResult : OrderPicklistExtra, username: string) => {
  if (extraResult) {
    const updateResult : any = await API.graphql({
      query: updateDgtPdOrderPicklistExtraReturn,
      variables: { input: {
        _version: extraResult._version, 
        id: extraResult.id,
        status: ExtraReturnStatus.QA_CHECKED,
        type: extraResult.type,
        qualityCheckedDate: formatDateObject(new Date()),
        qualityCheckedDateTime: Math.floor(Date.now() / 1000),
        qualityCheckedBy: username
      }}
    })
    return updateResult.data.updateDgtPdOrderPicklistExtraReturn
  }
  throw Error('not found')
}

export const rollbackQcCheckPicklist = async (latestHeader: OrderPicklist) => {
  if (latestHeader) {
    const updatedHeader = await updatePicklist(latestHeader.pk, 'DETAIL', {
      status: PicklistStatus.PD_CHECKED,
      orderType: latestHeader.orderType,
      division: latestHeader.division,
      qualityCheckedDate: null,
      qualityCheckedDateTime: null,
      qualityCheckedBy: null
    }, latestHeader._version!)
    return updatedHeader
  }
}

interface PicklistVar extends GrapQlVariable {
  releasedDate?: string
  requestedDate?: string
  approvedDate?: string
  approvedDateTime?: {}
  withdrawedDate?: string
  checkedDate?: string
  checkedDateTime?: {}
  qualityCheckedDate?: string,
  recordedDate?: string
  completedDate?: string
  canceledDate?: string
  wasteRecordedDate?: string
  bulkStatusDate?: string
}

export const getReleasedOrderPicklists = async (date: string, div: string, type: string) => {
  let variables : PicklistVar = {
    releasedDate: date,
    filter: {
      division: { eq: div },
      status: { eq: PicklistStatus.PD_RELEASED }
    },
    limit: 800
  }
  if (type && type !== OrderType.All) {
    const mixingOrFilling = type === 'Mixing' ? 'BULK' : 'FG'
    variables.filter = { ...variables.filter, materialType: { eq: mixingOrFilling }}
  }

  const queryResult: any = await API.graphql({
    query: picklistByReleaseDate,
    variables: variables
  })
  return Promise.resolve(queryResult.data?.picklistByReleaseDate.items)
}

export const getRequestedOrderPicklists = async (
  date: string, div: string, type: string, showAllStatus?: boolean, asc?: boolean, withExtra?: boolean) => {
  let variables : PicklistVar = {
    requestedDate: date,
    filter: {
      division: { eq: div },
      status: { eq: PicklistStatus.PD_REQUESTED }
    },
    limit: 500,
    sortDirection: 'DESC'
  }
  if (asc) delete variables.sortDirection

  if (showAllStatus) {
    // remove status filter
    variables.filter = { division: { eq: div } }
  }
  if (type && type !== OrderType.All) {
    const mixingOrFilling = type === 'Mixing' ? 'BULK' : 'FG'
    variables.filter = { ...variables.filter, materialType: { eq: mixingOrFilling }}
  }

  const queryResult: any = await API.graphql({
    query: picklistByRequestedDate,
    variables: variables
  })

  const pdApprovedPicklists = queryResult.data?.picklistByRequestedDate.items

  if (withExtra) {
    // Query Extra
    let extraVars : PicklistVar = {
      approvedDate: date,
      filter: {
        status: { eq: ExtraReturnStatus.APPROVED },
        type: { eq: ExtraReturnType.EXTRA }
      },
      limit: 500,
      sortDirection: 'DESC'
    }
    if (asc) delete extraVars.sortDirection
    if (type && type !== OrderType.All) {
      const prefixOrderNo = type === 'Mixing' ? '8' : '9'
      extraVars.filter = { ...extraVars.filter, orderId: { beginsWith: prefixOrderNo }}
    }
    const queryPlExtraResult: any = await API.graphql({
      query: extraReturnByApprovedDate,
      variables: extraVars
    })

    const pdApprovedPicklistExtras: DgtPdOrderPicklistExtraReturn[] = queryPlExtraResult.data?.extraReturnByApprovedDate.items

    const extraToPicklists = pdApprovedPicklistExtras
      .filter(item => {
        const orderInfo : DgtPdOrderPicklist = JSON.parse(item.orderInfo)
        return orderInfo.division === div
      }).map(extra => mapExtraToPicklist(extra))
    return Promise.resolve([...pdApprovedPicklists, ...extraToPicklists])
  } else {
    return Promise.resolve(pdApprovedPicklists)
  }
}

export const getRequestedOrderPicklistsV2 = async (
  date: string, div: string, type: string, status: PicklistStatus, withExtra?: boolean
) => { // type both bulk, Mixing / fg, Filling
  const typeEnum = decodeOrderType(type)

  const queryResult: any = await API.graphql(
    graphqlOperation(picklistByRequestedDateType, {
      requestedDate: date,
      divisionOrderTypeStatus: { 
        eq: { division: div, orderType: typeEnum, status }
      },
      limit: 500
    })
  );

  const pdApprovedPicklists = queryResult.data?.picklistByRequestedDateType.items
  if (withExtra) {
    const extraResult: any = await API.graphql(
      graphqlOperation(extraReturnByApprovedDateTypeStatus, {
        approvedDate: date,
        typeStatus: {
          eq: { 
            type: ExtraReturnType.EXTRA,
            status: status === PicklistStatus.WH_SHORTAGE ? ExtraReturnStatus.WAIT_SHORTAGE : ExtraReturnStatus.APPROVED
          }
        },
        limit: 100
      })
    );
    const pdApprovedPicklistExtras: DgtPdOrderPicklistExtraReturn[] = extraResult.data?.extraReturnByApprovedDateTypeStatus.items

    const extraToPicklists = pdApprovedPicklistExtras
      .filter(item => {
        const orderInfo : DgtPdOrderPicklist = JSON.parse(item.orderInfo)
        return orderInfo.division === div && orderInfo.orderType === typeEnum
      }).map(extra => mapExtraToPicklist(extra))
    
    const mergedPicklists : DgtPdOrderPicklist[]  = [...pdApprovedPicklists, ...extraToPicklists]
    mergedPicklists.sort((a, b) => a.requestedDateTime! > b.requestedDateTime! ? 1 : -1)
    return Promise.resolve(mergedPicklists)
  } else {
    return Promise.resolve(pdApprovedPicklists)
  }
}

export const getShortagedOrderPicklists = async (
  date: string, div: string, type: string, showAllStatus?: boolean, withExtra?: boolean) => {
  let variables : PicklistVar = {
    requestedDate: date,
    filter: {
      division: { eq: div },
      status: { eq: PicklistStatus.WH_SHORTAGE }
    },
    limit: 500
  }

  if (showAllStatus) {
    // remove status filter
    variables.filter = { division: { eq: div } }
  }
  if (type && type !== OrderType.All) {
    const mixingOrFilling = type === 'Mixing' ? 'BULK' : 'FG'
    variables.filter = { ...variables.filter, materialType: { eq: mixingOrFilling }}
  }

  const queryResult: any = await API.graphql({
    query: picklistByRequestedDate,
    variables: variables
  })
  const pdApprovedPicklists = queryResult.data?.picklistByRequestedDate.items

  if (withExtra) {
    // Query Extra
    let extraVars : PicklistVar = {
      approvedDate: date,
      filter: {
        status: { eq: ExtraReturnStatus.WAIT_SHORTAGE },
        type: { eq: ExtraReturnType.EXTRA }
      },
      limit: 500
    }
    if (type && type !== OrderType.All) {
      const prefixOrderNo = type === 'Mixing' ? '8' : '9'
      extraVars.filter = { ...extraVars.filter, orderId: { beginsWith: prefixOrderNo }}
    }
    const queryPlExtraResult: any = await API.graphql({
      query: extraReturnByApprovedDate,
      variables: extraVars
    })
    const pdApprovedPicklistExtras: DgtPdOrderPicklistExtraReturn[] = queryPlExtraResult.data?.extraReturnByApprovedDate.items

    const extraToPicklists = pdApprovedPicklistExtras
      .filter(item => {
        const orderInfo : DgtPdOrderPicklist = JSON.parse(item.orderInfo)
        return orderInfo.division === div
      }).map(extra => mapExtraToPicklist(extra))
    return Promise.resolve([...pdApprovedPicklists, ...extraToPicklists])
  } else {
    return Promise.resolve(pdApprovedPicklists)
  }
}

export const getIssuedOrderPicklists = async (
  date: string, div: string, type: string, showAllStatus?: boolean, withExtra?: boolean) => {
  let variables : PicklistVar = {
    approvedDate: date,
    filter: {
      division: { eq: div },
      status: { eq: PicklistStatus.WH_APPROVED }
    },
    limit: 500
  }

  if (showAllStatus) {
    // remove status filter
    variables.filter = { division: { eq: div } }
  }
  if (type && type !== OrderType.All) {
    const mixingOrFilling = ['Mixing', 'bulk'].includes(type) ? 'BULK' : 'FG'
    variables.filter = { ...variables.filter, materialType: { eq: mixingOrFilling }}
  }

  const queryResult: any = await API.graphql({
    query: picklistByApprovedDate,
    variables: variables
  })

  const whIssuedPicklists = queryResult.data?.picklistByApprovedDate.items

  if (withExtra) {
    // Query Extra
    let extraVars : PicklistVar = {
      withdrawedDate: date,
      filter: {
        status: { eq: ExtraReturnStatus.WITHDRAWED },
        type: { eq: ExtraReturnType.EXTRA }
      },
      limit: 500
    }
    if (type && type !== OrderType.All) {
      const prefixOrderNo = type === 'Mixing' ? '8' : '9'
      extraVars.filter = { ...extraVars.filter, orderId: { beginsWith: prefixOrderNo }}
    }
    const queryPlExtraResult: any = await API.graphql({
      query: extraReturnByWithdrawedDate,
      variables: extraVars
    })
    const whIssuedPicklistExtras: DgtPdOrderPicklistExtraReturn[] = queryPlExtraResult.data?.extraReturnByWithdrawedDate.items

    const extraToPicklists = whIssuedPicklistExtras
      .filter(item => {
        const orderInfo : DgtPdOrderPicklist = JSON.parse(item.orderInfo)
        return orderInfo.division === div
      }).map(extra => mapExtraToPicklist(extra))
    const finalPicklists = [...whIssuedPicklists, ...extraToPicklists] as DgtPdOrderPicklist[]
    finalPicklists.sort((a, b) => a.approvedDateTime! > b.approvedDateTime! ? 1 : -1)
    return Promise.resolve(finalPicklists)
  } else {
    return Promise.resolve(whIssuedPicklists)
  }
}

export const getPDCheckedOrderPicklists = async (date: string, div: string, type: string, showAllStatus?: boolean) => {
  let variables : PicklistVar = {
    checkedDate: date,
    filter: {
      division: { eq: div },
      status: { eq: PicklistStatus.PD_CHECKED }
    },
    limit: 500,
    sortDirection: 'DESC'
  }
  if (showAllStatus) {
    // remove status filter
    variables.filter = { division: { eq: div } }
  }
  if (type && type !== OrderType.All) {
    const mixingOrFilling = type === 'Mixing' ? 'BULK' : 'FG'
    variables.filter = { ...variables.filter, materialType: { eq: mixingOrFilling }}
  }

  const queryPlResult: any = await API.graphql({
    query: picklistByCheckedDate,
    variables: variables
  })
  const pdCheckedPicklists = queryPlResult.data?.picklistByCheckedDate.items
  // Query Extra
  const extraVar = variables
  extraVar.filter = {
    status: { eq: ExtraReturnStatus.CHECKED },
    type: { eq: ExtraReturnType.EXTRA }
  }
  if (type && type !== OrderType.All) {
    const prefixOrderNo = type === 'Mixing' ? '8' : '9'
    extraVar.filter = { ...extraVar.filter, orderId: { beginsWith: prefixOrderNo }}
  }
  const queryPlExtraResult: any = await API.graphql({
    query: extraReturnByCheckedDate,
    variables: extraVar
  })
  const pdCheckedPicklistExtras: DgtPdOrderPicklistExtraReturn[] = queryPlExtraResult.data?.extraReturnByCheckedDate.items

  const extraToPicklists = pdCheckedPicklistExtras
    .filter(item => {
      const orderInfo : DgtPdOrderPicklist = JSON.parse(item.orderInfo)
      return orderInfo.division === div
    }).map(extra => mapExtraToPicklist(extra))
  return Promise.resolve([...pdCheckedPicklists, ...extraToPicklists])
}

export const getQACheckedOrderPicklists = async (date: string, div: string, showAllStatus?: boolean) => {
  let variables : PicklistVar = {
    qualityCheckedDate: date,
    filter: {
      materialType: { eq: 'BULK' },
      division: { eq: div },
      status: { eq: PicklistStatus.QA_CHECKED }
    },
    limit: 500,
    sortDirection: 'DESC'
  }
  if (showAllStatus) {
    // remove status filter
    variables.filter = { division: { eq: div } }
  }

  const queryPlResult: any = await API.graphql({
    query: picklistByQualityChecked,
    variables: variables
  })
  const qaCheckedPicklists = queryPlResult.data?.picklistByQualityChecked.items
  // Query Extra
  const extraVar = variables
  extraVar.filter = {
    status: { eq: ExtraReturnStatus.QA_CHECKED },
    type: { eq: ExtraReturnType.EXTRA }
  }
  const queryPlExtraResult: any = await API.graphql({
    query: extraReturnByQualityChecked,
    variables: extraVar
  })
  const qaCheckedPicklistExtras: DgtPdOrderPicklistExtraReturn[] = queryPlExtraResult.data?.extraReturnByQualityChecked.items

  const extraToPicklists = qaCheckedPicklistExtras
    .filter(item => {
      const orderInfo : DgtPdOrderPicklist = JSON.parse(item.orderInfo)
      return orderInfo.division === div
    }).map(extra => mapExtraToPicklist(extra))
  return Promise.resolve([...qaCheckedPicklists, ...extraToPicklists])
}

const mapExtraToPicklist = (extra: DgtPdOrderPicklistExtraReturn) : DgtPdOrderPicklist => {
  const orderInfo : DgtPdOrderPicklist = JSON.parse(extra.orderInfo)
  return { ...orderInfo,
    requestedBy: extra.approvedBy, 
    requestedDateTime: extra.approvedDateTime,
    approvedBy: extra.withdrawedBy,
    approvedDateTime: extra.withdrawedDateTime,
    checkedBy: extra.checkedBy ? extra.checkedBy : undefined,
    checkedDateTime: extra.checkedDateTime ? extra.checkedDateTime : undefined,
    qualityCheckedBy: extra.qualityCheckedBy ? extra.qualityCheckedBy : undefined,
    qualityCheckedDateTime: extra.qualityCheckedDateTime ? extra.qualityCheckedDateTime : undefined,
    kiNo: extra?.kiNo ? extra.kiNo : undefined,
    commentText: `[เบิกเพิ่ม] ${extra.remark || ''}`,
    extraReturnId: extra.id
  }
}

export const getIssuedMovements = async (date: string, div: string, type: string) => {
  let vars : PicklistVar = {
    approvedDate: date,
    approvedDateTime: { gt: 0 },
    filter: {
      division: { eq: div },
      movementType: { eq: "261" },
      checkedDateTime: { attributeExists: false }
    },
    limit: 1000
  }
  if (type) {
    vars.filter = { ...vars.filter, 
      materialType: { eq: type }
    }
  }

  let nxtTkn = null
  let totalMvs: DgtPdOrderPicklistMovement[] = []
  do {
    if (nxtTkn) vars = { ...vars, nextToken: nxtTkn }
    const queryResult: any = await API.graphql({
      query: movementsByApprovedDate,
      variables: vars
    })
    const whIssuedMovements = queryResult.data?.movementsByApprovedDate.items as DgtPdOrderPicklistMovement[]
    nxtTkn = queryResult.data?.movementsByApprovedDate.nextToken
    totalMvs = [...totalMvs, ...whIssuedMovements]
  } while (nxtTkn !== null);
  
  return Promise.resolve(totalMvs)
}

export const getPdCheckeddMovements = async (date: string, div: string, type: string) => {
  let vars : PicklistVar = {
    checkedDate: date,
    checkedDateTime: { gt: 0 },
    filter: {
      division: { eq: div },
      movementType: { eq: "261" }
    },
    limit: 1000
  }
  if (type) {
    vars.filter = { ...vars.filter, 
      materialType: { eq: type }
    }
  }

  let nxtTkn = null
  let totalMvs: DgtPdOrderPicklistMovement[] = []
  do {
    if (nxtTkn) vars = { ...vars, nextToken: nxtTkn }
    const queryResult: any = await API.graphql({
      query: movementsByCheckedDate,
      variables: vars
    })
    const pdCheckedMovements = queryResult.data?.movementsByCheckedDate.items as DgtPdOrderPicklistMovement[]
    nxtTkn = queryResult.data?.movementsByCheckedDate.nextToken
    totalMvs = [...totalMvs, ...pdCheckedMovements]
  } while (nxtTkn !== null);
  
  return Promise.resolve(totalMvs)
}

export const getPdRecordedOrderPicklists = async (date: string, div: string, type: string) => {
  let variables : PicklistVar = {
    recordedDate: date,
    filter: {
      division: { eq: div },
      status: { eq: PicklistStatus.PD_RECORDED }
    },
    limit: 500
  }

  if (type && type !== OrderType.All) {
    const mixingOrFilling = type === 'Mixing' ? 'BULK' : 'FG'
    variables.filter = { ...variables.filter, materialType: { eq: mixingOrFilling }}
  }

  const queryResult: any = await API.graphql({
    query: picklistByRecordedDate,
    variables: variables
  })

  const pdRecordedPicklists = queryResult.data?.picklistByRecordedDate.items

  return Promise.resolve(pdRecordedPicklists)
}

export const getCompletedOrderPicklists = async (date: string, div: string, type: string) => {
  let variables : PicklistVar = {
    completedDate: date,
    filter: {
      division: { eq: div },
      status: { eq: PicklistStatus.PICKLIST_COMPLETED }
    },
    limit: 500
  }

  if (type && type !== OrderType.All) {
    const mixingOrFilling = type === 'Mixing' ? 'BULK' : 'FG'
    variables.filter = { ...variables.filter, materialType: { eq: mixingOrFilling }}
  }

  const queryResult: any = await API.graphql({
    query: picklistByCompletedDate,
    variables: variables
  })

  const pdCompletedPicklists = queryResult.data?.picklistByCompletedDate.items

  return Promise.resolve(pdCompletedPicklists)
}

export const getRequestedExtraPicklists = async (date: string, type: string) => {
  let extraVars : PicklistVar = {
    requestedDate: date,
    filter: {
      status: { eq: ExtraReturnStatus.NEW },
      type: { eq: ExtraReturnType.EXTRA }
    },
    limit: 500
  }
  if (type && type !== OrderType.All) {
    const prefixOrderNo = type === 'Mixing' ? '8' : '9'
    extraVars.filter = { ...extraVars.filter, orderId: { beginsWith: prefixOrderNo }}
  }
  const queryPlExtraResult: any = await API.graphql({
    query: extraReturnByRequestedDate,
    variables: extraVars
  })
  return Promise.resolve(queryPlExtraResult.data?.extraReturnByRequestedDate.items)
}

export const getPdApprovedExtraPicklists = async (date: string, type: string) => {
  let extraVars : PicklistVar = {
    approvedDate: date,
    filter: {
      status: { eq: ExtraReturnStatus.APPROVED },
      type: { eq: ExtraReturnType.EXTRA }
    },
    limit: 500
  }
  if (type && type !== OrderType.All) {
    const prefixOrderNo = type === 'Mixing' ? '8' : '9'
    extraVars.filter = { ...extraVars.filter, orderId: { beginsWith: prefixOrderNo }}
  }
  const queryPlExtraResult: any = await API.graphql({
    query: extraReturnByApprovedDate,
    variables: extraVars
  })
  return Promise.resolve(queryPlExtraResult.data?.extraReturnByApprovedDate.items)
}

export const getCanceledExtraPicklists = async (date: string, type: string) => {
  let extraVars : PicklistVar = {
    canceledDate: date,
    filter: {
      status: { eq: ExtraReturnStatus.CANCELED },
      type: { eq: ExtraReturnType.EXTRA }
    },
    limit: 500
  }
  if (type && type !== OrderType.All) {
    const prefixOrderNo = type === 'Mixing' ? '8' : '9'
    extraVars.filter = { ...extraVars.filter, orderId: { beginsWith: prefixOrderNo }}
  }
  const queryPlExtraResult: any = await API.graphql({
    query: extraReturnByCanceledDate,
    variables: extraVars
  })
  return Promise.resolve(queryPlExtraResult.data?.extraReturnByCanceledDate.items)
}

export const getRequestedReturnPicklists = async (date: string) => {
  let returnVars : PicklistVar = {
    requestedDate: date,
    filter: {
      status: { eq: ExtraReturnStatus.NEW },
      type: { eq: ExtraReturnType.RETURN }
    },
    limit: 500
  }

  const queryPlReturnResult: any = await API.graphql({
    query: extraReturnByRequestedDate,
    variables: returnVars
  })
  return Promise.resolve(queryPlReturnResult.data?.extraReturnByRequestedDate.items)
}

export const getWhApprovedReturnPicklists = async (date: string) => {
  let returnVars : PicklistVar = {
    approvedDate: date,
    filter: {
      status: { eq: ExtraReturnStatus.APPROVED },
      type: { eq: ExtraReturnType.RETURN }
    },
    limit: 500
  }

  const queryPlReturnResult: any = await API.graphql({
    query: extraReturnByApprovedDate,
    variables: returnVars
  })
  return Promise.resolve(queryPlReturnResult.data?.extraReturnByApprovedDate.items)
}

export const getCanceledReturnPicklists = async (date: string) => {
  let returnVars : PicklistVar = {
    canceledDate: date,
    filter: {
      status: { eq: ExtraReturnStatus.CANCELED },
      type: { eq: ExtraReturnType.RETURN }
    },
    limit: 500
  }

  const queryPlReturnResult: any = await API.graphql({
    query: extraReturnByCanceledDate,
    variables: returnVars
  })
  return Promise.resolve(queryPlReturnResult.data?.extraReturnByCanceledDate.items)
}

export const getReturnItem = async (rawId : string) : Promise<OrderPicklistExtraItem> => {
  const getResult: any = await API.graphql({
    query: getDgtPdOrderPicklistExtraReturnItem,
    variables: { id: rawId }
  })
  return getResult.data?.getDgtPdOrderPicklistExtraReturnItem
}

export const updateQtyReturnItem = async (rawId: string, newQty: number, version: number)
: Promise<OrderPicklistExtraItem> => {
  const updateResult : any = await API.graphql({
    query: updateDgtPdOrderPicklistExtraReturnItem,
    variables: { input: {
      _version: version,
      id: rawId,
      qty: newQty
    }}
  })
  return updateResult.data.updateDgtPdOrderPicklistExtraReturnItem
}

export const updateWithShrinkQtyReturnItem = async (rawId: string, newQty: number, version: number)
: Promise<OrderPicklistExtraItem> => {
  const updateResult : any = await API.graphql({
    query: updateDgtPdOrderPicklistExtraReturnItem,
    variables: { input: {
      _version: version, 
      id: rawId,
      withShQty: newQty
    }}
  })
  return updateResult.data.updateDgtPdOrderPicklistExtraReturnItem
}

export const updateWasteQtyReturnItem = async (rawId: string, newQty: number, version: number)
: Promise<OrderPicklistExtraItem> => {
  const updateResult : any = await API.graphql({
    query: updateDgtPdOrderPicklistExtraReturnItem,
    variables: { input: {
      _version: version, 
      id: rawId,
      wasteQty: newQty
    }}
  })
  return updateResult.data.updateDgtPdOrderPicklistExtraReturnItem
}

export const updateLotReturnItem = async (rawId: string, lot: string, version: number)
: Promise<OrderPicklistExtraItem> => {
  const updateResult : any = await API.graphql({
    query: updateDgtPdOrderPicklistExtraReturnItem,
    variables: { input: {
      _version: version, 
      id: rawId,
      batchLotNo: lot
    }}
  })
  return updateResult.data.updateDgtPdOrderPicklistExtraReturnItem
}

export const updateReturnReasonCodeReturnItem = async (rawId: string, code: string, desc: string, version: number)
: Promise<OrderPicklistExtraItem> => {
  const updateResult : any = await API.graphql({
    query: updateDgtPdOrderPicklistExtraReturnItem,
    variables: { input: {
      _version: version, 
      id: rawId,
      returnReasonCode: code,
      returnReasonDescription: desc
    }}
  })
  return updateResult.data.updateDgtPdOrderPicklistExtraReturnItem
}

export const returnPicklistsByOrderId = async (orderId: string) => {
  const extraResults: any = await API.graphql({
    query: extraReturnByOrderId,
    variables: { orderId, filter: { type: { eq: ExtraReturnType.RETURN } } }
  })
  const returns: DgtPdOrderPicklistExtraReturn[] = extraResults.data?.extraReturnByOrderId.items
  returns.sort((a, b) => (a.requestedDateTime || 0) - (b.requestedDateTime || 0))
  return returns
}

export const createExtraDIWMovement = async (orderId: string, refExtraId : string, div: string, amount: number) => {
  const postResult : any = await API.graphql({
    query: createDgtPdOrderPicklistMovement,
    variables: { input: {
      orderId,
      sk: `118003#261#${refExtraId}`,
      materialCode: '118003',
      materialDescription: 'DIW',
      batchLotNo: '',
      materialUnit: 'KG',
      materialType: 'RAW',
      documentYear: '',
      documentNo: '',
      documentItemNo: '',
      documentCreatedAt: 0,
      movementType: '261',
      amount: amount,
      materialRequestQty: amount,
      division: div
    } }
  })
  if (postResult.data) return postResult.data.createDgtPdOrderPicklistMovement
  throw new Error('Create movement error')
}

export const updateStoLocReturnItem = async (rawId: string, stLoc: string, version: number)
: Promise<OrderPicklistExtraItem> => {
  const updateResult : any = await API.graphql({
    query: updateDgtPdOrderPicklistExtraReturnItem,
    variables: { input: {
      _version: version, 
      id: rawId,
      returnStorageLocation: stLoc && stLoc !== '-' ? stLoc : null
    }}
  })
  return updateResult.data.updateDgtPdOrderPicklistExtraReturnItem
}

export const updateRemarkReturnItem = async (rawId: string, remark: string, version: number)
: Promise<OrderPicklistExtraItem> => {
  const updateResult : any = await API.graphql({
    query: updateDgtPdOrderPicklistExtraReturnItem,
    variables: { input: {
      _version: version, 
      id: rawId,
      remark
    }}
  })
  return updateResult.data.updateDgtPdOrderPicklistExtraReturnItem
}

export const newReturnItem = async (
  returnDocId: string,
  newItem : DgtPdOrderPicklistExtraReturnItem,
  qty: number, 
  withShQty: number,
  wasteQty: number
  ) : Promise<OrderPicklistExtraItem> => {
  const postItem : any = await API.graphql({
    query: createDgtPdOrderPicklistExtraReturnItem,
    variables: { input: {
      extraReturnId: returnDocId,
      orderId: newItem.orderId,
      materialCode: newItem.materialCode,
      materialDescription: newItem.materialDescription,
      materialUnit: newItem.materialUnit,
      batchLotNo: newItem.batchLotNo,
      qty,
      withShQty,
      wasteQty,
      returnStorageLocation: newItem.returnStorageLocation
    }}
  })
  if (postItem.data) return postItem.data.createDgtPdOrderPicklistExtraReturnItem
  throw new Error('Create Return Item error')
}

export const inActiveReturnItem = async (itemId: string, version: number)
: Promise<OrderPicklistExtraItem> => {
  const updateResult : any = await API.graphql({
    query: updateDgtPdOrderPicklistExtraReturnItem,
    variables: { input: {
      id: itemId,
      _version: version, 
      active: false
    }}
  })
  return updateResult.data.updateDgtPdOrderPicklistExtraReturnItem
}

export const activeReturnItem = async (itemId: string, version: number)
: Promise<OrderPicklistExtraItem> => {
  const updateResult : any = await API.graphql({
    query: updateDgtPdOrderPicklistExtraReturnItem,
    variables: { input: {
      id: itemId,
      _version: version, 
      active: true
    }}
  })
  return updateResult.data.updateDgtPdOrderPicklistExtraReturnItem
}

export const whSplitReturnPicklist = async (
  originalReturn: DgtPdOrderPicklistExtraReturn,
  moveItemIds: string[]) => {
  // CLONE Original RETURN to new one
  const cloneReturn = {
    orderId: originalReturn.orderId,
    orderInfo: originalReturn.orderInfo,
    type: ExtraReturnType.RETURN,
    requestedDate: originalReturn.requestedDate,
    requestedBy : originalReturn.requestedBy,
    requestedDateTime : originalReturn.requestedDateTime
  }
  const postReturnHeader : any = await API.graphql({
    query: createDgtPdOrderPicklistExtraReturn,
    variables: { input: cloneReturn }
  })

  if (postReturnHeader.data) {
    const returnDoc = postReturnHeader.data.createDgtPdOrderPicklistExtraReturn
    const returnId = returnDoc.id
    const updateParentReturnToItems = moveItemIds.map(id => updateParentIdToItem(id, returnId))
    await Promise.allSettled(updateParentReturnToItems)

    return returnId
  }
  throw new Error('Create return error')
}

export const updateParentIdToItem = async (id: string, newParentId: string)
: Promise<OrderPicklistExtraItem> => {
  const returnItem = await getReturnItem(id)
  if (returnItem) {
    const updateResult : any = await API.graphql({
      query: updateDgtPdOrderPicklistExtraReturnItem,
      variables: { input: {
        _version: returnItem._version, 
        id: id,
        extraReturnId: newParentId
      }}
    })
    return updateResult.data.updateDgtPdOrderPicklistExtraReturnItem
  }
  throw new Error('not found')
}

export const getWasteReturnMovements = async (date: string) => {
  let vars : PicklistVar = {
    wasteRecordedDate: date,
    filter: {},
    limit: 1000
  }

  let nxtTkn = null
  let totalMvs: DgtPdOrderPicklistMovement[] = []
  do {
    if (nxtTkn) vars = { ...vars, nextToken: nxtTkn }
    const queryResult: any = await API.graphql({
      query: movementsByWasteRecorded,
      variables: vars
    })
    const wasteReturnMovements = queryResult.data?.movementsByWasteRecorded.items as DgtPdOrderPicklistMovement[]
    nxtTkn = queryResult.data?.movementsByWasteRecorded.nextToken
    totalMvs = [...totalMvs, ...wasteReturnMovements]
  } while (nxtTkn !== null);
  
  return Promise.resolve(totalMvs)
}

export const whApproveWasteReturnMovement = async (mv: OrderPicklistMovement, username: string) => {
  if (mv) {
    return await updateMovement(mv.orderId, mv.sk, {
      approvedWasteReturnDate: formatDateObject(new Date()),
      approvedWasteReturnDateTime: mv.approvedWasteReturnDateTime,
      approvedWasteReturnBy: username,
      wasteRecordedStatus: WasteRecordStatus.QC_APPROVED,
    }, mv._version!)
  }
}

export const whRejectWasteReturnMovement = async (mv: OrderPicklistMovement) => {
  if (mv) {
    return await updateMovement(mv.orderId, mv.sk, {
      wasteRecordedDate: null,
      wasteRecordedDateTime: null,
      wasteRecordedBy: null,
      wasteRecordedStatus: null,
    }, mv._version!)
  }
}

export const getBulkInspectionLogs = async (date: string) => {
  let vars : PicklistVar = {
    bulkStatusDate: date,
    filter: {},
    limit: 1000
  }

  let nxtTkn = null
  let totalOperationLogs: DgtPdOrderOperationLog[] = []
  do {
    if (nxtTkn) vars = { ...vars, nextToken: nxtTkn }
    const queryResult: any = await API.graphql({
      query: orderLogByBulkStatusDate,
      variables: vars
    })
    const logs = queryResult.data?.orderLogByBulkStatusDate.items as DgtPdOrderOperationLog[]
    nxtTkn = queryResult.data?.orderLogByBulkStatusDate.nextToken
    totalOperationLogs = [...totalOperationLogs, ...logs]
  } while (nxtTkn !== null);
  
  return Promise.resolve(totalOperationLogs)
}

export const qcApproveBulkInspectionLogs = async (ol: OrderOperationLog, username: string) => {
  if (ol) {
    return await updateOrderOperationLog(ol.id, {
      approveBulkStatusDate: formatDateObject(new Date()),
      approveBulkStatusDateTime: ol.approveBulkStatusDateTime,
      approveBulkStatusBy: username
    }, ol._version!)
  }
}

const decodeOrderType = (type: string) => {
  if (type === OrderTypeEnum.BULK.toLocaleLowerCase() || type === OrderType.Mixing)
    return OrderTypeEnum.BULK
  else if (type === OrderTypeEnum.CLAIM.toLocaleLowerCase()) 
    return OrderTypeEnum.CLAIM
  else if (type === OrderTypeEnum.REPACK.toLocaleLowerCase()) 
    return OrderTypeEnum.REPACK

  return OrderTypeEnum.FG
}

export const getClaimOrders = async (
  dateFrom: string, dateTo: string
) : Promise<DgtPdProductionOrder[]> => {
  const queryResult: any = await API.graphql(
    graphqlOperation(orderByTypeSortByDate, {
      orderType: OrderTypeEnum.CLAIM,
      createdAt: {
        between: [dateFrom, dateTo]
      }
    })
  );

  const orders = queryResult.data?.orderByTypeSortByDate.items
  return Promise.resolve(orders)
}

export const getFGInspectionLogs = async (orderId: string, wcId: string) => {
  const inspectLogResult: any = await API.graphql({
    query: inspectionLogByOrderAndWc,
    variables: { productionOrderId: orderId, workCenterId: {
      eq: wcId
    }}
  })
  const inspectLogs: OrderInspectionLog[] = inspectLogResult.data?.inspectionLogByOrderAndWc.items
  return inspectLogs
}

export const getFGInspectionLogsByOrder = async (orderId: string) => {
  const inspectLogResult: any = await API.graphql({
    query: inspectionLogByOrderAndWc,
    variables: { productionOrderId: orderId }
  })
  const inspectLogs: OrderInspectionLog[] = inspectLogResult.data?.inspectionLogByOrderAndWc.items
  return inspectLogs
}

export const getOrderReceipts = async (orderId: string) => {
  const inspectLogResult: any = await API.graphql({
    query: orderReceiptByOrder,
    variables: { productionOrderId: orderId }
  })
  const receipts: OrderReceipt[] = inspectLogResult.data?.orderReceiptByOrder.items
  window.localStorage.setItem('lastUpdatedReceipts', formatDateObject(new Date(), 'dd.MM.yyyy HH:mm'))
  return receipts
}

export const updateIssueQtyOrderReceipt = async (receiptId: string, issueField:string, newQty: number, version: number)
: Promise<OrderReceipt> => {
  const updateResult : any = await API.graphql({
    query: updateDgtPdOrderReceipt,
    variables: { input: {
      _version: version,
      id: receiptId,
      [issueField]: newQty
    }}
  })
  return updateResult.data.updateDgtPdOrderReceipt
}

export const updateRejectAndNoteOrderReceipt = async (receiptId: string, rejectQty: number, note: string, version: number)
: Promise<OrderReceipt> => {
  const updateResult : any = await API.graphql({
    query: updateDgtPdOrderReceipt,
    variables: { input: {
      _version: version,
      id: receiptId,
      reject: rejectQty,
      remark: note
    }}
  })
  return updateResult.data.updateDgtPdOrderReceipt
}

export const updateOtherAndNoteOrderReceipt = async (receiptId: string, otherQty: number, note: string, version: number)
: Promise<OrderReceipt> => {
  const updateResult : any = await API.graphql({
    query: updateDgtPdOrderReceipt,
    variables: { input: {
      _version: version,
      id: receiptId,
      otherIssue: otherQty,
      otherIssueNote: note
    }}
  })
  return updateResult.data.updateDgtPdOrderReceipt
}

export const updateEaPerPackOrderReceipt = async (receipts: OrderReceipt[], eaPerPack: number)
: Promise<OrderReceipt[]> => {
  const updateReceiptPromises = receipts?.map(rep => API.graphql({ 
    query: updateDgtPdOrderReceipt,
    variables: { input: {
      _version: rep._version,
      id: rep.id,
      infoEaPerPack: eaPerPack
    }}
  }))

  const results = await Promise.allSettled(updateReceiptPromises)
  const updatedReceipts = (results
    .filter(res => res.status === 'fulfilled') as PromiseFulfilledResult<UpdateOrdReceiptGraphQLResponse>[])
    .map(r => r.value.data!.updateDgtPdOrderReceipt)
  return updatedReceipts
}

export const updateTotalAmountEAOrderReceipt = async (receiptId: string, newAmount: number, version: number)
: Promise<OrderReceipt> => {
  const updateResult : any = await API.graphql({
    query: updateDgtPdOrderReceipt,
    variables: { input: {
      _version: version,
      id: receiptId,
      totalAmount: newAmount
    }}
  })
  return updateResult.data.updateDgtPdOrderReceipt
}

export const updateBulkUsageOrderReceipt = async (receiptId: string, newBulkUsage: number, version: number)
: Promise<OrderReceipt> => {
  const updateResult : any = await API.graphql({
    query: updateDgtPdOrderReceipt,
    variables: { input: {
      _version: version,
      id: receiptId,
      bulkUsage: newBulkUsage
    }}
  })
  return updateResult.data.updateDgtPdOrderReceipt
}

type UpdateOrdReceiptGraphQLResponse = {
  data?: {
    updateDgtPdOrderReceipt: OrderReceipt
  }
  error?: any
}
export const pdApproveOrderReceipts = async (orderReceipts: OrderReceipt[], username: string)
: Promise<OrderReceipt[]> => {
  const allPromises = orderReceipts.map(receipt =>
    API.graphql({
      query: updateDgtPdOrderReceipt,
      variables: { input: {
        _version: receipt._version,
        id: receipt.id,
        approvedByPdDate: formatDateObject(new Date()),
        approvedByPdDatetime: Math.floor(Date.now() / 1000),
        approvedByPdBy: username,
        currentApprover: receipt.productionOrderId.startsWith('9') ? ReceiptApprover.QC : null // up currentApprover to QC only FG
      }}
    })
  )
  const results = await Promise.allSettled(allPromises)
  const updatedReceipts = (results
    .filter(res => res.status === 'fulfilled') as PromiseFulfilledResult<UpdateOrdReceiptGraphQLResponse>[])
    .map(r => r.value.data!.updateDgtPdOrderReceipt)
  return updatedReceipts
}

export const otherApproveOrderReceipts = async (orderReceipts: OrderReceipt[], department: Department, username: string)
: Promise<OrderReceipt[]> => {

  const approvedByPdPayload = {
    approvedByPdDate: formatDateObject(new Date()),
    approvedByPdDatetime: Math.floor(Date.now() / 1000),
    approvedByPdBy: username,
    currentApprover: ReceiptApprover.QC,
    isRework: false,
    reworkedBy: undefined,
    reworkedDatetime: undefined,
    // reworkNote: keep note for history
  }

  const approvedByQcPayload = {
    approvedByQcDate: formatDateObject(new Date()),
    approvedByQcDatetime: Math.floor(Date.now() / 1000),
    approvedByQcBy: username,
    currentApprover: ReceiptApprover.WH,
    isRework: false,
    reworkedBy: undefined,
    reworkedDatetime: undefined,
    // reworkNote: keep note for history
  }

  const approvedByWhPayload = {
    approvedByWhDate: formatDateObject(new Date()),
    approvedByWhDatetime: Math.floor(Date.now() / 1000),
    approvedByWhMonth: formatMonthObject(new Date()),
    approvedByWhBy: username,
    currentApprover: ReceiptApprover.FA,
    isRework: false,
    reworkedBy: undefined,
    reworkedDatetime: undefined,
    // reworkNote: keep note for history
  }

  const approvedByFaPayload = {
    approvedByFaDate: formatDateObject(new Date()),
    approvedByFaDatetime: Math.floor(Date.now() / 1000),
    approvedByFaBy: username,
    currentApprover: null,
    isRework: false,
    reworkedBy: undefined,
    reworkedDatetime: undefined,
    // reworkNote: keep note for history
  }
  const updatePayload = 
    department === Department.QC ? approvedByQcPayload :
    department === Department.WAREHOUSE ? approvedByWhPayload :
    department === Department.FA ? approvedByFaPayload :
    department === Department.PRODUCTION ? approvedByPdPayload : {}

  const allPromises = orderReceipts.map(receipt =>
    API.graphql({
      query: updateDgtPdOrderReceipt,
      variables: { input: {
        _version: receipt._version,
        id: receipt.id,
        // How to update by department
        ...updatePayload
      }}
    })
  )
  const results = await Promise.allSettled(allPromises)
  const updatedReceipts = (results
    .filter(res => res.status === 'fulfilled') as PromiseFulfilledResult<UpdateOrdReceiptGraphQLResponse>[])
    .map(r => r.value.data!.updateDgtPdOrderReceipt)
  return updatedReceipts
}

export const otherRejectOrderReceipts = async (orderReceipts: OrderReceipt[], rejectToDepartment: ReceiptApprover, rejectComment: string, rejectedBy: string)
: Promise<OrderReceipt[]> => {
  const rejectToPDPayload = {
    isRework: true,
    reworkedBy: rejectedBy,
    reworkedDatetime: Math.floor(Date.now() / 1000),
    reworkNote: rejectComment,
    currentApprover: ReceiptApprover.PD
  }

  const rejectToQcPayload = {
    isRework: true,
    reworkedBy: rejectedBy,
    reworkedDatetime: Math.floor(Date.now() / 1000),
    reworkNote: rejectComment,
    currentApprover: ReceiptApprover.QC
  }

  const rejectToWhPayload = {
    isRework: true,
    reworkedBy: rejectedBy,
    reworkedDatetime: Math.floor(Date.now() / 1000),
    reworkNote: rejectComment,
    currentApprover: ReceiptApprover.WH
  }

  const allPromises = orderReceipts.map(receipt =>
    API.graphql({
      query: updateDgtPdOrderReceipt,
      variables: { input: {
        _version: receipt._version,
        id: receipt.id,
        // How to update by department
        ...rejectToDepartment === ReceiptApprover.PD ? rejectToPDPayload :
        rejectToDepartment === ReceiptApprover.QC ? rejectToQcPayload :
        rejectToDepartment === ReceiptApprover.WH ? rejectToWhPayload : {}
      }}
    })
  )
  const results = await Promise.allSettled(allPromises)
  const updatedReceipts = (results
    .filter(res => res.status === 'fulfilled') as PromiseFulfilledResult<UpdateOrdReceiptGraphQLResponse>[])
    .map(r => r.value.data!.updateDgtPdOrderReceipt)
  return updatedReceipts
}

export const getApprovalOrderReceipts = async (
  department: Department | undefined,
  fromDate?: string, 
  toDate?: string
) : Promise<OrderReceipt[]> => {
  let currentApprover = ReceiptApprover.PD
  if (department === Department.QC) currentApprover = ReceiptApprover.QC
  else if (department === Department.WAREHOUSE) currentApprover = ReceiptApprover.WH
  else if (department === Department.FA) currentApprover = ReceiptApprover.FA

  let approvalOrderReceipts : OrderReceipt[] = []
  let nxtTkn = null
  if (fromDate && toDate) {
    let vars = {
      currentApprover,
      limit: 1000,
      updatedAt: {
        between: [ fromDate, toDate ] 
      },
      nextToken: undefined
    }
    do {
      if (nxtTkn) vars = { ...vars, nextToken: nxtTkn }
      const queryWithRangeResult: any = await API.graphql(
        graphqlOperation(receiptByCurrentApprover, vars)
      );
      const receipts = queryWithRangeResult.data?.receiptByCurrentApprover.items as OrderReceipt[]
      nxtTkn = queryWithRangeResult.data?.receiptByCurrentApprover.nextToken
      approvalOrderReceipts = [...approvalOrderReceipts, ...receipts]
    } while (nxtTkn !== null);
  } else {
    const queryResult: any = await API.graphql(
      graphqlOperation(receiptByCurrentApprover, {
        currentApprover,
        limit: 2000,
      })
    );
    approvalOrderReceipts = queryResult.data?.receiptByCurrentApprover.items
  }
  
  return Promise.resolve(approvalOrderReceipts)
}

// let vars : PicklistVar = {
//   bulkStatusDate: date,
//   filter: {},
//   limit: 1000
// }

// let nxtTkn = null
// let totalOperationLogs: DgtPdOrderOperationLog[] = []
// do {
//   if (nxtTkn) vars = { ...vars, nextToken: nxtTkn }
//   const queryResult: any = await API.graphql({
//     query: orderLogByBulkStatusDate,
//     variables: vars
//   })
//   const logs = queryResult.data?.orderLogByBulkStatusDate.items as DgtPdOrderOperationLog[]
//   nxtTkn = queryResult.data?.orderLogByBulkStatusDate.nextToken
//   totalOperationLogs = [...totalOperationLogs, ...logs]
// } while (nxtTkn !== null);

// return Promise.resolve(totalOperationLogs)

export const getWhApprovedOrderReceipts = async (date: string) => {
  const queryResult: any = await API.graphql({
    query: receiptByApprovedByWhDate,
    variables: { approvedByWhDate: date, limit: 500 }
  });

  const receipts = queryResult.data?.receiptByApprovedByWhDate.items
  return Promise.resolve(receipts)
}

export const getFaApprovedOrderReceipts = async (date: string) => {
  const queryResult: any = await API.graphql({
    query: receiptByApprovedByFaDate,
    variables: { approvedByFaDate: date, limit: 500 }
  });

  const receipts = queryResult.data?.receiptByApprovedByFaDate.items
  return Promise.resolve(receipts)
}

export const getBulkItems = async (orderId : string) : Promise<OrderPicklist[]> => {
  const bulkResult: any = await API.graphql({
    query: listDgtPdOrderPicklists,
    variables: { 
      pk: orderId, sk: { beginsWith: 'ITEM#' },
      filter: {
        materialType: { eq: 'BULK' }
      }
    }
  })
  return bulkResult.data?.listDgtPdOrderPicklists.items
}

export const updateStatusComment = async (orderId: string, skComment: string, active: boolean, version: number) => {
  const postResult : any = await API.graphql({
    query: updateDgtPdOrderPicklist,
    variables: { input: {
      _version: version, 
      pk: orderId, 
      sk: skComment,
      commentActive: active
    }}
  })
  if (postResult.data) return postResult.data.updateDgtPdOrderPicklist
  throw new Error('Update item error')
}

export const deleteReceipt = async (receiptId: string, version: number)
: Promise<OrderReceipt> => {
  const deleteResult : any = await API.graphql({
    query: deleteDgtPdOrderReceipt,
    variables: { input: {
      _version: version,
      id: receiptId
    }}
  })
  return deleteResult.data.deleteDgtPdOrderReceipt
}

export const deleteInspectionLog = async (logId: string, version: number)
: Promise<OrderInspectionLog> => {
  const deleteResult : any = await API.graphql({
    query: deleteDgtPdOrderInspectionLog,
    variables: { input: {
      _version: version,
      id: logId
    }}
  })
  return deleteResult.data.deleteDgtPdOrderInspectionLog
}
